import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { merge, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilKeyChanged } from 'rxjs/operators';
import { CohortModalComponent } from '../../cohort-modal/cohort-modal.component';
import { Cohort, CohortService } from '../cohort.service';

@Injectable()
export class SelectedCohortService implements OnDestroy {
  routeSubscription: Subscription;
  selectionContext: SelectedCohortContext;

  constructor(
    private cohortService: CohortService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.routeSubscription = this.route.queryParams
      .pipe(distinctUntilKeyChanged('selectedCohort'))
      .subscribe(async (params) => {
        const selectedCohort = params['selectedCohort'];

        if (selectedCohort) {
          if (!this.selectionContext) {
            this.selectionContext = new SelectedCohortContext(null, true);
          }

          this.selectionContext.cohort =
            this.selectionContext.cohort ??
            (await this.cohortService.getCohort(selectedCohort));

          this.openCohortModal();
        }
      });
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
  }

  selectCohort(cohort: Cohort, sticky = false): SelectedCohortContext {
    this.selectionContext = new SelectedCohortContext(cohort, sticky);

    if (sticky) {
      this.updateQueryParams(cohort.name);
    } else {
      this.openCohortModal();
    }

    return this.selectionContext;
  }

  private openCohortModal() {
    const modal = this.modalService.open(CohortModalComponent, {
      size: 'lg',
      windowClass: 'lytxModal',
      centered: true,
    });

    modal.componentInstance.cohort = this.selectionContext.cohort;

    this.selectionContext.setModal(modal);

    merge(
      this.selectionContext.closed,
      this.selectionContext.dismissed,
    ).subscribe(() => {
      if (this.selectionContext.sticky) {
        this.updateQueryParams();
      }
    });
  }

  private updateQueryParams(cohortName: string = null) {
    this.router.navigate([], {
      queryParams: { selectedCohort: cohortName },
      queryParamsHandling: 'merge',
    });
  }
}

export class SelectedCohortContext {
  modal: NgbModalRef;
  sticky = false;
  cohort: Cohort;

  _closed: Subject<void> = new Subject();
  _dismissed: Subject<void> = new Subject();

  constructor(cohort: Cohort, sticky = false) {
    this.cohort = cohort;
    this.sticky = sticky;
  }

  get closed(): Observable<any> {
    return this._closed.asObservable();
  }

  get dismissed(): Observable<any> {
    return this._dismissed.asObservable();
  }

  setModal(modal: NgbModalRef) {
    this.modal = modal;

    this.modal.closed.subscribe(() => {
      console.log('modal closed');
      this._closed.next();
      this._closed.complete();
    });

    this.modal.dismissed.subscribe(() => {
      console.log('modal dismissed');
      this._dismissed.next();
      this._dismissed.complete();
    });
  }
}
